import { youtube_v3 } from "googleapis";
import moment from "moment";

// Format duration from ISO 8601
export const formatDuration = (isoDuration: string): string => {
  const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return "0:00";

  const hours = match[1] ? parseInt(match[1], 10) : 0;
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const seconds = match[3] ? parseInt(match[3], 10) : 0;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  } else {
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }
};

// Format count for displaying (e.g., 1.2K, 4.5M)
export const formatCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  } else if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
};

// Calculate performance metrics for a video
export const calculatePerformanceMetrics = (video: youtube_v3.Schema$Video) => {
  if (!video || !video.statistics) return null;

  const views = parseInt(video.statistics?.viewCount || "0", 10);
  const likes = parseInt(video.statistics?.likeCount || "0", 10);
  const comments = parseInt(video.statistics?.commentCount || "0", 10);

  // Extract duration from contentDetails
  const duration = video.contentDetails?.duration || "PT0M0S";
  const durationInMinutes = (() => {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (match) {
      const hours = parseInt(match[1] || "0", 10);
      const minutes = parseInt(match[2] || "0", 10);
      const seconds = parseInt(match[3] || "0", 10);
      return hours * 60 + minutes + seconds / 60;
    }
    return 0;
  })();

  const engagementPerMinute =
    durationInMinutes > 0
      ? ((likes + comments) / durationInMinutes).toFixed(2)
      : "0";
  const engagementRate =
    views > 0 ? (((likes + comments) / views) * 100).toFixed(2) : "0";

  return {
    views,
    likes,
    comments,
    engagementPerMinute,
    engagementRate,
    duration: formatDuration(duration),
  };
};

// Extract video ID from URL
export const extractVideoId = (url: string): string | null => {
  // Handle URLs like:
  // https://www.youtube.com/watch?v=VIDEO_ID
  // https://youtu.be/VIDEO_ID
  // https://www.youtube.com/embed/VIDEO_ID
  // Also handles additional parameters like &t=1s, &feature=share, etc.

  const standardRegex =
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(standardRegex);
  if (match) {
    return match[1];
  }

  return null;
};

// Extract channel ID from URL
export const extractChannelId = (url: string): string | null => {
  // Handle URLs like:
  // https://www.youtube.com/channel/UCknMR7NOY6ZKcVbyzOxQPhw

  const channelRegex = /youtube\.com\/channel\/([^\/\?]+)/;
  const match = url.match(channelRegex);
  if (match) {
    return match[1];
  }

  return null;
};
